import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import CatalogService, { CHAVES_CATALOGO } from '../api/CatalogService';
import SkinProfileService, { CHAVES_PERFIL_PELE } from '../api/SkinProfileService';
import { CatalogProduct } from '../types/entities';
import { SkinTag } from '../types/enums';

/**
 * Produtos oferecidos para uma etapa da rotina.
 *
 * O backend devolve a categoria inteira já na ordem certa (o que o usuário tem vem
 * primeiro). O "ver mais" é estado da tela, não outra requisição — por isso o hook
 * entrega a lista completa e a gaveta fatia.
 *
 * O perfil de pele vem da mesma chave que o dashboard e as configurações usam, então
 * normalmente já está em cache; serve só para o selo "indicado para a sua pele".
 */
export default function useProductSuggestions(categoryId: string | null, enabled: boolean) {
  const { userId } = useCurrentUser();
  const habilitado = Boolean(userId) && Boolean(categoryId) && enabled;

  const query = useQuery({
    queryKey: CHAVES_CATALOGO.daEtapa(userId!, categoryId!),
    queryFn: async () => (await CatalogService.suggestions(userId!, categoryId!)).data ?? [],
    enabled: habilitado,
  });

  const perfilQuery = useQuery({
    queryKey: CHAVES_PERFIL_PELE.perfil(userId!),
    queryFn: async () => (await SkinProfileService.getProfile(userId!)).data ?? null,
    enabled: habilitado,
  });

  const tipoDePele = perfilQuery.data?.skinType as unknown as SkinTag | undefined;

  /**
   * Selo honesto no lugar de um "% de compatibilidade": ou o produto é indicado para
   * o tipo de pele do usuário, ou não temos o que afirmar.
   */
  const combinaComVoce = (produto: CatalogProduct): boolean =>
    Boolean(tipoDePele) &&
    produto.recommendedFor.some((t) => t === SkinTag.ALL || t === tipoDePele);

  return {
    products: query.data ?? [],
    combinaComVoce,
    loading: query.isPending,
    error: query.isError ? 'Não foi possível carregar os produtos desta etapa.' : '',
  };
}
